import { escape } from './html.js';
import { t, getLanguage } from './i18n.js';
import { absoluteUrl, alternatesFor } from './url.js';
import { site } from '../data/site.js';
import { games } from '../data/games.js';
import { services } from '../data/services.js';
import { projects } from '../data/projects.js';
import { posts, findPost, postPath } from '../data/posts.js';
import { artworkManifest } from '../data/artwork.js';
import { LOCALES, getLocale } from '../i18n/locales.js';

const ORGANIZATION_ID = `${site.url}/#organization`;
const WEBSITE_ID = `${site.url}/#website`;
const OG_WIDTH = 1200;
const OG_HEIGHT = 630;
const DEFAULT_OG_IMAGE = '/assets/images/og/netbyte.jpg';
const OG_IMAGES = {
  home: '/assets/images/og/netbyte-home.jpg',
  games: '/assets/images/og/netbyte-games.jpg',
  lab: '/assets/images/og/netbyte-lab.jpg',
};
/** Every post has its own card, written by `npm run og`. */
export const postOgImage = (post) => `/assets/images/og/blog-${post.slug}.jpg`;

const PAGE_TYPES = {
  games: 'CollectionPage',
  lab: 'CollectionPage',
  services: 'CollectionPage',
  contact: 'ContactPage',
  press: 'AboutPage',
  blog: 'CollectionPage',
};

const BREADCRUMB_KEYS = {
  home: 'nav.home',
  games: 'nav.games',
  lab: 'nav.lab',
  services: 'nav.services',
  blog: 'nav.blog',
  press: 'nav.press',
  sustainability: 'nav.sustainability',
  contact: 'nav.contact',
  imprint: 'nav.imprint',
  privacy: 'nav.privacy',
  terms: 'nav.terms',
};

const storeUrls = (game) => Object.values(game.stores).filter(Boolean);

/** The copy keys behind a route's title and description. */
const metaKeys = (route) =>
  route.post
    ? { title: `post.${route.post}.title`, description: `post.${route.post}.description` }
    : { title: `meta.${route.meta}.title`, description: `meta.${route.meta}.description` };

const routeTitle = (route) =>
  route.post ? `${t(metaKeys(route).title)} – ${site.name}` : t(metaKeys(route).title);

const blogPosting = (post, locale, extra = {}) => ({
  '@type': 'BlogPosting',
  headline: t(`post.${post.slug}.title`),
  description: t(`post.${post.slug}.description`),
  url: absoluteUrl(postPath(post), locale.code),
  datePublished: post.published,
  dateModified: post.updated ?? post.published,
  inLanguage: locale.htmlLang,
  image: `${site.url}${postOgImage(post)}`,
  author: { '@type': 'Person', name: site.director },
  publisher: { '@id': ORGANIZATION_ID },
  ...extra,
});

const organizationNode = () => ({
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: site.legalName,
  alternateName: site.name,
  url: `${site.url}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${site.url}/assets/images/apple-icon-180x180.png`,
    width: 180,
    height: 180,
  },
  email: site.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: site.address.street,
    postalCode: site.address.postalCode,
    addressLocality: site.address.city,
    addressCountry: site.address.countryCode,
  },
  founder: { '@type': 'Person', name: site.director },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: site.email,
    availableLanguage: LOCALES.map((locale) => locale.htmlLang),
  },
});

const websiteNode = () => ({
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${site.url}/`,
  name: site.name,
  inLanguage: LOCALES.map((locale) => locale.htmlLang),
  publisher: { '@id': ORGANIZATION_ID },
});

const itemList = (entries) => ({
  '@type': 'ItemList',
  itemListElement: entries.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item,
  })),
});

const pageEntity = (route, locale, pageId) => {
  if (route.post) {
    return blogPosting(findPost(route.post), locale, {
      '@id': `${absoluteUrl(route.path, locale.code)}#article`,
      mainEntityOfPage: { '@id': pageId },
    });
  }

  if (route.meta === 'blog') {
    return itemList(posts.map((post) => blogPosting(post, locale)));
  }

  if (route.meta === 'games') {
    return itemList(
      games.map((game) => ({
        '@type': 'VideoGame',
        name: t(`game.${game.slug}.title`),
        genre: t(game.genreKey),
        gamePlatform: game.platforms,
        image: `${site.url}/assets/images/${artworkManifest[game.art].source}`,
        ...(storeUrls(game).length ? { sameAs: storeUrls(game) } : {}),
      })),
    );
  }

  if (route.meta === 'lab') {
    return itemList(
      projects.map((project) => ({
        '@type': 'CreativeWork',
        name: project.name,
        genre: t(`lab.${project.slug}.kind`),
        description: t(`lab.${project.slug}.description`),
        image: `${site.url}/assets/images/${artworkManifest[project.art].source}`,
        creator: { '@id': ORGANIZATION_ID },
      })),
    );
  }

  if (route.meta === 'services') {
    return itemList(
      services.map((service) => ({
        '@type': 'Service',
        name: t(`services.${service.slug}.title`),
        description: t(`services.${service.slug}.description`),
        provider: { '@id': ORGANIZATION_ID },
      })),
    );
  }

  return null;
};

const buildGraph = ({ route, locale, canonical, title, description, image }) => {
  const graph = [organizationNode(), websiteNode()];
  if (!route) return { '@context': 'https://schema.org', '@graph': graph };

  const pageId = `${canonical}#webpage`;
  const page = {
    '@type': PAGE_TYPES[route.meta] ?? 'WebPage',
    '@id': pageId,
    url: canonical,
    name: title,
    description,
    inLanguage: locale.htmlLang,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: image.url,
      width: image.width,
      height: image.height,
    },
  };

  const mainEntity = pageEntity(route, locale, pageId);
  if (mainEntity) page.mainEntity = mainEntity;

  if (route.path !== '/') {
    const trail = [
      { name: t('nav.home'), item: absoluteUrl('/', locale.code) },
      ...(route.post
        ? [
            { name: t('nav.blog'), item: absoluteUrl('/blog', locale.code) },
            { name: t(metaKeys(route).title), item: canonical },
          ]
        : [{ name: t(BREADCRUMB_KEYS[route.meta]), item: canonical }]),
    ];
    page.breadcrumb = { '@id': `${canonical}#breadcrumb` };
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: trail.map((entry, index) => ({ '@type': 'ListItem', position: index + 1, ...entry })),
    });
  }

  graph.push(page);
  return { '@context': 'https://schema.org', '@graph': graph };
};

/**
 * Everything the document head needs for one route in the active locale.
 * Used by the build (to write static HTML) and by the router (after navigation).
 */
export const buildMeta = (route = null, path = '/') => {
  const locale = getLocale(getLanguage());
  const isNotFound = !route;
  const canonical = isNotFound ? null : absoluteUrl(path, locale.code);
  const post = route?.post ? findPost(route.post) : null;
  const title = isNotFound ? `${t('notfound.label')} – ${site.name}` : routeTitle(route);
  const description = isNotFound ? t('notfound.description') : t(metaKeys(route).description);
  const imagePath = (post && postOgImage(post)) || (!isNotFound && OG_IMAGES[route.meta]) || DEFAULT_OG_IMAGE;
  const image = {
    url: `${site.url}${imagePath}`,
    width: OG_WIDTH,
    height: OG_HEIGHT,
    alt: title,
  };

  return {
    locale,
    isNotFound,
    path,
    title,
    description,
    canonical,
    robots: isNotFound ? 'noindex, follow' : 'index, follow, max-image-preview:large',
    ogType: post ? 'article' : 'website',
    article: post
      ? { published: post.published, modified: post.updated ?? post.published, author: site.director }
      : null,
    image,
    alternates: isNotFound ? [] : alternatesFor(path),
    jsonLd: buildGraph({ route, locale, canonical, title, description, image }),
  };
};

/** Open Graph article properties and the `meta.article` field each one reads. */
const ARTICLE_TAGS = { published_time: 'published', modified_time: 'modified', author: 'author' };

const jsonLd = (value) => JSON.stringify(value, null, 2).replace(/</g, '\\u003c');

/** Serialise the head block for the prerendered pages. */
export const headTags = (meta) => {
  const lines = [
    `  <meta name="description" content="${escape(meta.description)}" />`,
    `  <meta name="robots" content="${escape(meta.robots)}" />`,
  ];

  if (meta.canonical) {
    lines.push(`  <link rel="canonical" href="${escape(meta.canonical)}" />`);
    meta.alternates.forEach((alternate) => {
      lines.push(
        `  <link rel="alternate" hreflang="${escape(alternate.hreflang)}" href="${escape(alternate.href)}" />`,
      );
    });
  }

  lines.push(
    `  <meta property="og:type" content="${escape(meta.ogType)}" />`,
    `  <meta property="og:site_name" content="${escape(site.name)}" />`,
  );
  if (meta.article) {
    Object.entries(ARTICLE_TAGS).forEach(([property, field]) => {
      lines.push(`  <meta property="article:${property}" content="${escape(meta.article[field])}" />`);
    });
  }
  if (meta.canonical) lines.push(`  <meta property="og:url" content="${escape(meta.canonical)}" />`);
  lines.push(
    `  <meta property="og:title" content="${escape(meta.title)}" />`,
    `  <meta property="og:description" content="${escape(meta.description)}" />`,
    `  <meta property="og:image" content="${escape(meta.image.url)}" />`,
    '  <meta property="og:image:type" content="image/jpeg" />',
    `  <meta property="og:image:width" content="${meta.image.width}" />`,
    `  <meta property="og:image:height" content="${meta.image.height}" />`,
    `  <meta property="og:image:alt" content="${escape(meta.image.alt)}" />`,
    `  <meta property="og:locale" content="${escape(meta.locale.ogLocale)}" />`,
    '  <meta name="twitter:card" content="summary_large_image" />',
    `  <meta name="twitter:title" content="${escape(meta.title)}" />`,
    `  <meta name="twitter:description" content="${escape(meta.description)}" />`,
    `  <meta name="twitter:image" content="${escape(meta.image.url)}" />`,
    `  <meta name="twitter:image:alt" content="${escape(meta.image.alt)}" />`,
    '  <script type="application/ld+json" id="page-schema">',
    jsonLd(meta.jsonLd)
      .split('\n')
      .map((line) => `  ${line}`)
      .join('\n'),
    '  </script>',
  );

  return lines.join('\n');
};

const setMeta = (attribute, name, content) => {
  if (!content) return;
  const selector = `meta[${attribute}="${name}"]`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const syncCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    canonical?.remove();
    return;
  }
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', href);
};

const syncAlternates = (alternates) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
  alternates.forEach((alternate) => {
    const element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', alternate.hreflang);
    element.setAttribute('href', alternate.href);
    document.head.appendChild(element);
  });
};

const syncJsonLd = (value) => {
  let script = document.head.querySelector('script#page-schema');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-schema';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(value);
};

/** Keep title, description, social tags, canonical, hreflang and JSON-LD in step with the route. */
export const applyRouteMeta = (route, path = '/') => {
  if (typeof document === 'undefined') return;
  const meta = buildMeta(route, path);

  document.title = meta.title;
  setMeta('name', 'description', meta.description);
  setMeta('name', 'robots', meta.robots);
  setMeta('property', 'og:type', meta.ogType);
  Object.entries(ARTICLE_TAGS).forEach(([property, field]) => {
    if (meta.article) setMeta('property', `article:${property}`, meta.article[field]);
    else document.head.querySelector(`meta[property="article:${property}"]`)?.remove();
  });
  setMeta('property', 'og:site_name', site.name);
  setMeta('property', 'og:title', meta.title);
  setMeta('property', 'og:description', meta.description);
  setMeta('property', 'og:image', meta.image.url);
  setMeta('property', 'og:image:type', 'image/jpeg');
  setMeta('property', 'og:image:width', String(meta.image.width));
  setMeta('property', 'og:image:height', String(meta.image.height));
  setMeta('property', 'og:image:alt', meta.image.alt);
  setMeta('property', 'og:locale', meta.locale.ogLocale);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', meta.title);
  setMeta('name', 'twitter:description', meta.description);
  setMeta('name', 'twitter:image', meta.image.url);
  setMeta('name', 'twitter:image:alt', meta.image.alt);

  if (meta.canonical) {
    setMeta('property', 'og:url', meta.canonical);
    syncCanonical(meta.canonical);
  } else {
    document.head.querySelector('meta[property="og:url"]')?.remove();
    syncCanonical(null);
  }

  // Tags from older builds and the previous OG-only locale list are not reused.
  document.head
    .querySelectorAll('meta[property^="twitter:"], meta[property="og:locale:alternate"]')
    .forEach((element) => element.remove());

  syncAlternates(meta.alternates);
  syncJsonLd(meta.jsonLd);
};
